// Checkbox rules for Add New Loan Step 6 "Demographic information", ported from
// loan-form-card/demographic-information/index.jsx as pure functions so the Dashboard applies
// the same exclusivity. Each takes the current per-person declarations object and the key
// that was clicked, and returns the next object.
//
// One Dashboard-only addition: the Dashboard hides sub-options while their parent is
// unchecked (the wizard always shows them), so unchecking a parent also clears its
// sub-options and "other" text — otherwise hidden answers would still be sent.

const HISPANIC_SUB = {
	hmdaMexicanIndicator: false,
	hmdaPuertoRicanIndicator: false,
	hmdaCubanIndicator: false,
	hmdaHispanicLatinoOtherOriginIndicator: false,
	hmdaOtherHispanicLatinoOrigin: "",
};

const AMERICAN_INDIAN_SUB = {
	hmdaAmericanIndianTribe: "",
	americanIndianOtherValue: "",
};

const ASIAN_SUB = {
	hmdaAsianIndianIndicator: false,
	hmdaChineseIndicator: false,
	hmdaFilipinoIndicator: false,
	hmdaJapaneseIndicator: false,
	hmdaKoreanIndicator: false,
	hmdaVietnameseIndicator: false,
	hmdaAsianOtherRaceIndicator: false,
	hmdaOtherAsianRace: "",
};

const PACIFIC_ISLANDER_SUB = {
	hmdaNativeHawaiianIndicator: false,
	hmdaGuamanianOrChamorroIndicator: false,
	hmdaSamoanIndicator: false,
	hmdaPacificIslanderOtherIndicator: false,
	hmdaOtherPacificIslanderRace: "",
};

// Race parents whose sub-options the Dashboard hides while unchecked.
const RACE_PARENT_SUB = {
	hmdaAmericanIndianIndicator: AMERICAN_INDIAN_SUB,
	hmdaAsianIndicator: ASIAN_SUB,
	hmdaPacificIslanderIndicator: PACIFIC_ISLANDER_SUB,
};

// Sub-option checkboxes whose "other" text box is hidden while unchecked.
const OTHER_TEXT_FOR = {
	hmdaHispanicLatinoOtherOriginIndicator: "hmdaOtherHispanicLatinoOrigin",
	hmdaAsianOtherRaceIndicator: "hmdaOtherAsianRace",
	hmdaPacificIslanderOtherIndicator: "hmdaOtherPacificIslanderRace",
};

// Sex answers are written under both key casings, as the wizard writes them.
const SEX_KEYS = {
	hmdaGendertypeFemaleIndicator: "HmdaGendertypeFemaleIndicator",
	hmdaGendertypeMaleIndicator: "HmdaGendertypeMaleIndicator",
	hmdaGendertypeDoNotWishIndicator: "HmdaGendertypeDoNotWishIndicator",
};

const setSex = (key, value) => {
	const out = {};
	Object.entries(SEX_KEYS).forEach(([lower, upper]) => {
		const v = lower === key ? value : false;
		out[lower] = v;
		out[upper] = v;
	});
	return out;
};

/**
 * Applies one demographic checkbox click.
 * @param {object} decl current per-person declarations/demographics
 * @param {string} key  the clicked checkbox key (lower-case `hmda…` form)
 * @returns {object} next declarations object
 */
export const toggleDemographic = (decl, key) => {
	const d = decl || {};
	const next = !d[key];

	switch (key) {
		case "hmdaEthnicityHispanicLatinoIndicator":
			return {
				...d,
				hmdaEthnicityHispanicLatinoIndicator: next,
				hmdaEthnicityNotHispanicLatinoIndicator: false,
				hmdaEthnicityDoNotWishIndicator: false,
				...(next ? {} : HISPANIC_SUB),
			};
		case "hmdaEthnicityNotHispanicLatinoIndicator":
			return {
				...d,
				hmdaEthnicityHispanicLatinoIndicator: false,
				...HISPANIC_SUB,
				hmdaEthnicityNotHispanicLatinoIndicator: next,
				hmdaEthnicityDoNotWishIndicator: false,
			};
		case "hmdaEthnicityDoNotWishIndicator":
			return next
				? {
						...d,
						hmdaEthnicityHispanicLatinoIndicator: false,
						...HISPANIC_SUB,
						hmdaEthnicityNotHispanicLatinoIndicator: false,
						hmdaEthnicityDoNotWishIndicator: true,
				  }
				: { ...d, hmdaEthnicityDoNotWishIndicator: false };
		case "hmdaRaceDoNotWishProvideIndicator":
			return next
				? {
						...d,
						hmdaAmericanIndianIndicator: false,
						...AMERICAN_INDIAN_SUB,
						hmdaAsianIndicator: false,
						...ASIAN_SUB,
						hmdaAfricanAmericanIndicator: false,
						hmdaPacificIslanderIndicator: false,
						...PACIFIC_ISLANDER_SUB,
						hmdaWhiteIndicator: false,
						hmdaRaceDoNotWishProvideIndicator: true,
				  }
				: { ...d, hmdaRaceDoNotWishProvideIndicator: false };
		case "hmdaAmericanIndianIndicator":
		case "hmdaAsianIndicator":
		case "hmdaAfricanAmericanIndicator":
		case "hmdaPacificIslanderIndicator":
		case "hmdaWhiteIndicator":
			return {
				...d,
				[key]: next,
				hmdaRaceDoNotWishProvideIndicator: false,
				...(!next && RACE_PARENT_SUB[key] ? RACE_PARENT_SUB[key] : {}),
			};
		default:
			if (SEX_KEYS[key]) return { ...d, ...setSex(key, next) };
			// Sub-options only toggle themselves, as in the wizard.
			return {
				...d,
				[key]: next,
				...(!next && OTHER_TEXT_FOR[key] ? { [OTHER_TEXT_FOR[key]]: "" } : {}),
			};
	}
};

/** Tribe text — the wizard writes it to both keys. */
export const setAmericanIndianTribe = (decl, value) => ({
	...(decl || {}),
	hmdaAmericanIndianTribe: value,
	americanIndianOtherValue: value,
});
